import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import firebase from '../db/firebase/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, getDoc,deleteField } from 'firebase/firestore';
import { ref as storageRef, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';


const RemoteSurveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModalInstallerId, setShowModalInstallerId] = useState(null);
  const [installerName, setInstallerName] = useState('');
  const [installerEmail, setInstallerEmail] = useState('');
  const [uploadingFor, setUploadingFor] = useState(null); // email del usuario actual
  const [pdfFile, setPdfFile] = useState(null); // archivo a subir
  const [showPdfModalEmail, setShowPdfModalEmail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const surveysRef = collection(firebase.db, 'remoteSurveys');
    const q = query(surveysRef, orderBy('createdAt', 'desc'));
  
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const baseData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  
      const enriched = await Promise.all(
        baseData.map(async (survey) => {
          try {
            const userRef = doc(firebase.db, 'userData', survey.email);
            const userSnap = await getDoc(userRef);
  
            const userData = userSnap.exists() ? userSnap.data() : {};
            return {
              ...survey,
              linkQuote: userData.linkQuote || null,
              dateSelectInstallation: userData.dateSelectInstallation || null,
            };
          } catch (err) {
            console.error('Error enriching survey:', err);
            return survey;
          }
        })
      );
  
      enriched.sort((a, b) => {
        if (a.state === b.state) {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        }
        return a.state === 'pending' ? -1 : 1;
      });
  
      setSurveys(enriched);
      setLoading(false);
    });
  
    return () => unsubscribe();
  }, []);

  const handlePdfUpload = async (survey) => {
    if (!pdfFile) return alert('Please select a PDF file');
    const email = survey.email;
    const fileName = `quote-${email}.pdf`;
    const filePath = `quotes/${email}/${fileName}`;
    const fileRef = storageRef(firebase.storage, filePath);
  
    const loadingToast = toast.loading('Uploading PDF...');
  
    try {
      await uploadBytes(fileRef, pdfFile);
      const url = await getDownloadURL(fileRef);
  
      await updateDoc(doc(firebase.db, 'userData', email), {
        linkQuote: url,
      });
  
      setSurveys(prev =>
        prev.map(s => (s.email === email ? { ...s, linkQuote: url } : s))
      );
  
      toast.update(loadingToast, { render: 'PDF uploaded successfully!', type: 'success', isLoading: false, autoClose: 3000 });
      setUploadingFor(null);
      setPdfFile(null);
    } catch (err) {
      console.error('Error uploading PDF:', err);
      toast.update(loadingToast, { render: 'Upload failed.', type: 'error', isLoading: false, autoClose: 3000 });
    }
  };
  const handleDeletePdf = async (email) => {
    const confirm = window.confirm('Are you sure you want to delete this PDF?');
    if (!confirm) return;
  
    const filePath = `quotes/${email}/quote-${email}.pdf`;
    const fileRef = storageRef(firebase.storage, filePath);
  
    const loadingToast = toast.loading('Deleting PDF...');
  
    try {
      await deleteObject(fileRef);
      await updateDoc(doc(firebase.db, 'userData', email), {
        linkQuote: deleteField(),
        dateSelectInstallation: deleteField(),
        
      });
  
      setSurveys(prev =>
        prev.map(s => (s.email === email ? { ...s, linkQuote: null } : s))
      );
  
      toast.update(loadingToast, { render: 'PDF deleted successfully!', type: 'success', isLoading: false, autoClose: 3000 });
    } catch (err) {
      console.error('Error deleting PDF:', err);
      toast.update(loadingToast, { render: 'Delete failed.', type: 'error', isLoading: false, autoClose: 3000 });
    }
  };

  const stateOrder = {
    pending: 0,
    'Quote Request Received': 1,
    'Quote In Generation': 2,
    'Quote Complete': 3,
  };
  
  const getStepFromState = (state) => stateOrder[state] ?? 0;
  
  const getStateFromStep = (step) => {
    const reverse = {
      1: 'Quote Request Received',
      2: 'Quote In Generation',
      3: 'Quote Complete',
    };
    return reverse[step] || 'pending';
  };
  
  const toggleSurveyState = async (survey, stepIndex) => {
    const currentStep = getStepFromState(survey.state);
    let newStep = stepIndex;
  
    // Si está activo y se toca, retrocede
    if (currentStep === stepIndex) newStep = stepIndex - 1;
  
    const newState = getStateFromStep(newStep);
  
    if (newState === 'Quote In Generation') {
      setInstallerName(survey.installerName || '');
      setInstallerEmail(survey.installerEmail || '');
      setShowModalInstallerId(survey.id);
      return;
    }
  
    try {
      await updateDoc(doc(firebase.db, 'remoteSurveys', survey.id), {
        state: newState,
      });
    } catch (err) {
      console.error('Error updating state: ', err);
    }
  };

  const handleDelete = async id => {
    const survey = surveys.find(s => s.id === id);
    if (!survey) return;
    if (!window.confirm('Are you sure you want to delete this survey and its images?')) return;

    // Delete images from Storage
    const images = survey.images || {};
    await Promise.all(
      Object.values(images).map(async url => {
        try {
          const path = decodeURIComponent(url.split('/o/')[1].split('?')[0]);
          const imgRef = storageRef(firebase.storage, path);
          await deleteObject(imgRef);
        } catch (err) {
          console.error('Error deleting image:', err);
        }
      })
    );

    // Delete document
    try {
      await deleteDoc(doc(firebase.db, 'remoteSurveys', id));
    } catch (error) {
      console.error('Error deleting survey: ', error);
    }
  };

  const filteredSurveys = surveys.filter(s => (filter === 'all' ? true : s.state === filter));

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-white z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-gray-800"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 h-screen overflow-y-auto bg-gray-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Remote Surveys</h2>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="border rounded p-2 bg-white"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="answered">Answered</option>
        </select>
      </div>
  
      <div className="flex items-center w-full bg-gray-100 p-4 rounded mb-4">
        <span className="flex-1 font-medium text-gray-700">Name</span>
        <span className="flex-1 font-medium text-gray-700">Email</span>
        <span className="flex-1 font-medium text-gray-700">Date</span>
        <span className="flex-1 font-medium text-gray-700">Quote PDF</span>
        <div className="w-60 flex justify-between font-medium text-gray-700 text-sm">
          <span>Received</span>
          <span>Generation</span>
          <span>Complete</span>
        </div>
        <span className="w-32 text-center font-medium text-gray-700">Actions</span>
      </div>
  
      {filteredSurveys.length === 0 ? (
        <p className="text-center text-gray-500 mt-10">No surveys to display.</p>
      ) : (
        <div className="space-y-4">
          {filteredSurveys.map(survey => (
            <React.Fragment key={survey.id}>
              <motion.div
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`w-full p-4 rounded shadow flex items-center transition-colors ${
                  survey.state === 'answered' || survey.state === 'Quote Complete'
                    ? 'bg-gray-200 opacity-50'
                    : 'bg-white'
                }`}
              >
                <p className="flex-1 font-semibold text-lg">{survey.name}</p>
  
                <div className="flex-1">
                  <p className="text-sm text-gray-600">{survey.email}</p>
                  {survey.installerName && (
                    <p className="text-sm text-gray-500 italic mt-0.5">
                      👷 Installer: {survey.installerName}
                    </p>
                  )}
                </div>
  
                <p className="flex-1 text-sm text-gray-500">
                  {survey.createdAt && survey.createdAt.toDate
                    ? survey.createdAt.toDate().toLocaleString()
                    : survey.createdAt}
                </p>
  
                {/* Quote PDF button with tick */}
                <div className="flex-1 text-sm text-gray-600">
                  <button
                    onClick={() => setShowPdfModalEmail(survey.email)}
                    className={`text-xs font-medium flex items-center gap-1 ${
                      survey.linkQuote ? 'text-green-600' : 'text-[#5298A8]'
                    } hover:underline`}
                  >
                    Quote PDF
                    {survey.linkQuote && <span className="text-lg">✔</span>}
                  </button>
                </div>
  
                <div className="w-60 flex justify-between items-center px-2">
                  {[1, 2, 3].map(step => {
                    const currentStep = getStepFromState(survey.state);
                    const isActive = step <= currentStep;
                    return (
                      <button
                        key={step}
                        onClick={() => toggleSurveyState(survey, step)}
                        className={`w-6 h-6 rounded-full transition ${
                          isActive ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                    );
                  })}
                </div>
  
                <div className="w-32 flex justify-center space-x-4">
                  <button
                    onClick={() => navigate(`/remote-surveys/${survey.id}`)}
                    className="text-[#5298A8] font-bold hover:underline"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleDelete(survey.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </motion.div>
  
              {/* Modal PDF */}
              {showPdfModalEmail === survey.email && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg w-[90%] max-w-md space-y-4 relative">
                    <button
                      onClick={() => {
                        setShowPdfModalEmail(null);
                        setPdfFile(null);
                      }}
                      className="absolute top-2 right-4 text-gray-600 text-xl font-bold"
                    >
                      ×
                    </button>
  
                    <h2 className="text-xl font-semibold mb-2 text-[#5298A8]">Quote PDF</h2>
  
                    {survey.linkQuote ? (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-600">A quote PDF is already uploaded.</p>

                            <a
                              href={survey.linkQuote}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#5298A8] underline text-sm font-medium"
                            >
                              View Current PDF
                            </a>

                            {/* Estado de instalación */}
                            {survey.linkQuote && !survey.dateSelectInstallation && (
                              <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                                <AlertCircle size={16} className="text-red-600" />
                                User has not confirmed installation
                              </p>
                            )}

                            {survey.dateSelectInstallation && (
                              <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                                <CheckCircle size={16} className="text-green-600" />
                                Installation confirmed:{" "}
                                {new Date(survey.dateSelectInstallation).toLocaleDateString('en-GB')}
                              </p>
                            )}

                            <div className="flex justify-end">
                              <button
                                onClick={() => {
                                  handleDeletePdf(survey.email);
                                  setShowPdfModalEmail(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                              >
                                <Trash2 size={16} />
                                Delete PDF
                              </button>
                            </div>
                          </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600">No PDF uploaded yet.</p>
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={e => setPdfFile(e.target.files[0])}
                            className="text-sm"
                          />
                          <button
                            onClick={() => {
                              handlePdfUpload(survey);
                              setShowPdfModalEmail(null);
                            }}
                            disabled={!pdfFile}
                            className="px-4 py-2 rounded bg-[#5298A8] text-white font-medium hover:opacity-90 disabled:opacity-50"
                          >
                            Upload PDF
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
  
      {/* Toast feedback */}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default RemoteSurveys;